// Connection, snapshot buffering/interpolation, local prediction + reconciliation.
import { storyOn } from './story-flags';
import { io, Socket } from 'socket.io-client';
import type { ClientToServer, ServerToClient, Snapshot, Welcome, PagerMsg, VehicleSnap, PlayerSnap, ActRequest } from '../../common/src/protocol';
import type { Character } from '../../common/src/rules';
import { vehicleMods, overclockMods } from '../../common/src/rules';
import { buildMap, GameMap, TILE } from '../../common/src/map';
import { InputCmd, PHYS_DT, stepFoot, stepVehicle, VState, PxRect, footBlockers, FootCar } from '../../common/src/sim';
import { VEHICLES, VehicleType, WORLD, WORLD_VERSION } from '../../common/src/data';
import { lerp, lerpAngle } from '../../common/src/util';

export const INTERP_DELAY = 100; // ms behind server for remote entities
/** The snapshot's cars for walking collision: the same list the server builds (room.ts footCars) */
const footCarsOf = (s: Snapshot | null | undefined): FootCar[] => (s?.v ?? []).map(v => ({ x: v.x, y: v.y, vx: v.vx, vy: v.vy, r: VEHICLES[v.t].radius }));
/** The content hash of this bundle (`main.js?v=…`, written by scripts/build.mjs). Sent when joining, so a tab still
 *  running code from before a deploy is asked to reload even when only code (not data) changed. */
export const BUILD = (() => {
  try { return new URL((document.querySelector('script[src*="main.js"]') as HTMLScriptElement).src).searchParams.get('v') ?? ''; } catch { return ''; }
})();

export interface CorrectionStats { reconciles: number; corrections: number; sumPx: number; maxPx: number; snaps: number; hist: number[] }

export class Net {
  socket!: Socket<ServerToClient, ClientToServer>;
  map: GameMap = buildMap();
  welcome: Welcome | null = null;
  char: Character | null = null;
  snaps: Snapshot[] = [];
  latest: Snapshot | null = null;
  clockOffset = 0; offsetInit = false; // serverTime ≈ Date.now() + offset
  rtt = 0;
  seq = 0;
  pending: InputCmd[] = [];
  sendBuf: InputCmd[] = [];
  // predicted self state
  foot = { x: 0, y: 0 }; prevFoot = { x: 0, y: 0 };
  veh: VState | null = null; prevVeh: VState | null = null; vehId = 0;
  visOff = { x: 0, y: 0 }; // decaying visual correction offset
  stats: CorrectionStats = { reconciles: 0, corrections: 0, sumPx: 0, maxPx: 0, snaps: 0, hist: [0, 0, 0, 0, 0] };
  onMsg: (m: PagerMsg) => void = () => {};
  onChar: (c: Character) => void = () => {};
  onUi: (u: { open: string; poi: string }) => void = () => {};
  onKicked: (why: string) => void = () => {};
  onSnap: (s: Snapshot) => void = () => {};
  connected = false;
  barriers: PxRect[] = [];
  // optional simulated network conditions (tests / manual latency checks)
  lag = { up: 0, down: 0, jitter: 0 }; private upAt = 0; private downAt = 0;
  private sentAt = new Map<number, number>();
  setLag(up: number, down: number, jitter: number) { this.lag = { up, down, jitter }; }
  private delay(kind: 'up' | 'down', fn: () => void) {
    const base = kind === 'up' ? this.lag.up : this.lag.down;
    if (!base && !this.lag.jitter) { fn(); return; }
    const now = performance.now();
    let at = now + base + Math.random() * this.lag.jitter;
    // keep order (TCP semantics): never deliver before a previously scheduled message
    if (kind === 'up') { at = Math.max(at, this.upAt); this.upAt = at; } else { at = Math.max(at, this.downAt); this.downAt = at; }
    setTimeout(fn, at - now);
  }
  private emitIn(cmds: InputCmd[]) { const sock = this.socket; this.delay('up', () => sock.emit('input', cmds)); }

  connect(token: string, room: string, mask = -1): Promise<Welcome> {
    return new Promise((res, rej) => {
      this.socket = io({ transports: ['websocket'], auth: { token, room, mask, world: WORLD_VERSION, build: BUILD, story: storyOn() }, reconnection: true, reconnectionDelay: 500, reconnectionDelayMax: 3000 });
      this.socket.on('welcome', w => { this.welcome = w; this.char = w.character; this.connected = true; this.pending = []; this.snaps = []; this.veh = null; this.primed = false; this.onChar(w.character); res(w); });
      this.socket.on('snap', s => this.delay('down', () => this.receive(s)));
      this.socket.on('char', c => { this.char = c; this.onChar(c); });
      this.socket.on('msg', m => this.onMsg(m));
      this.socket.on('ui', u => this.onUi(u));
      this.socket.on('kicked', why => { this.onKicked(why); this.socket.io.opts.reconnection = false; rej(new Error(why)); });
      this.socket.on('disconnect', () => { this.connected = false; });
      this.socket.on('connect_error', e => { if (!this.welcome) rej(e); });
    });
  }
  act(a: ActRequest) { const sock = this.socket; if (sock) this.delay('up', () => sock.emit('act', a)); }
  serverNow() { return Date.now() + this.clockOffset; }

  me(s = this.latest): PlayerSnap | undefined { return s?.p.find(p => p.id === s.you); }
  myVehicle(s = this.latest): VehicleSnap | undefined { const m = this.me(s); return m?.v ? s!.v.find(v => v.id === m.v) : undefined; }
  isDriver() { const m = this.me(); return !!m && m.v > 0 && m.s === 0; }

  private receive(s: Snapshot) {
    const off = s.t - Date.now();
    // track the minimum-latency offset estimate, slowly relaxing so drift is absorbed
    if (!this.offsetInit) { this.clockOffset = off; this.offsetInit = true; } else this.clockOffset = off > this.clockOffset ? this.clockOffset + (off - this.clockOffset) * 0.02 : off;
    this.snaps.push(s); if (this.snaps.length > 30) this.snaps.shift();
    this.latest = s;
    this.barriers = WORLD.barriers.filter((_, i) => s.inf.bar[i]).map(b => ({ x: b.x * TILE, y: b.y * TILE, w: b.w * TILE, h: b.h * TILE }));
    this.reconcile(s);
    this.onSnap(s);
  }

  /** Vehicle mods as the server steps them: the Ryujin Overclock flag (fx bit 64) comes with the snapshot. */
  private driveMods(t: VehicleType, fx: number) { const m = vehicleMods(this.char, t); return fx & 64 ? overclockMods(m) : m; }
  /** Reset predicted state to authoritative state and replay unacknowledged inputs. */
  private primed = false;
  private reconcile(s: Snapshot) {
    const me = this.me(s); if (!me) return;
    this.pending = this.pending.filter(c => c.seq > s.ack);
    for (const [q, t] of this.sentAt) if (q <= s.ack) { if (q > s.ack - 3) this.rtt = this.rtt ? this.rtt * 0.8 + (performance.now() - t) * 0.2 : performance.now() - t; this.sentAt.delete(q); }
    this.stats.reconciles++;
    const drivingId = me.v && me.s === 0 ? me.v : 0;
    let before: { x: number; y: number } | null = null, after: { x: number; y: number };
    if (drivingId) {
      const sv = s.v.find(v => v.id === drivingId)!;
      if (this.veh && this.vehId === drivingId) before = { x: this.veh.x, y: this.veh.y };
      const v: VState = { x: sv.x, y: sv.y, a: sv.a, vx: sv.vx, vy: sv.vy };
      const spec = VEHICLES[sv.t]; const mods = this.driveMods(sv.t, me.fx);
      const dead = !!(sv.f & 32); // EMP stall: the server ignores throttle, steering and handbrake
      for (const c of this.pending) stepVehicle(this.map, v, dead ? 0 : -c.my, dead ? 0 : c.mx, spec, mods, this.barriers, !dead && !!c.hb);
      this.veh = v; this.vehId = drivingId; after = { x: v.x, y: v.y };
      if (!before) { this.prevVeh = { ...v }; }
      this.foot = { x: v.x, y: v.y };
    } else {
      this.veh = null; this.vehId = 0;
      before = me.v ? null : { ...this.foot };
      const f = { x: me.x, y: me.y };
      if (!me.v && !me.d && !(me.fx & 2)) { const cars = footCarsOf(s); for (const c of this.pending) stepFoot(this.map, f, c.mx, c.my, me.spd, PHYS_DT, footBlockers(f, cars)); }
      this.foot = f; after = f;
      if (!before) this.prevFoot = { ...f };
    }
    if (!this.primed) { this.primed = true; before = null; this.prevFoot = { ...this.foot }; }
    if (before) {
      const dx = before.x - after.x, dy = before.y - after.y, d = Math.hypot(dx, dy);
      // >48px = snap (server teleport, respawn, vault, or a genuinely large divergence); counted separately
      if (d > 0.5 && d <= 48) {
        this.stats.corrections++; this.stats.sumPx += d; this.stats.maxPx = Math.max(this.stats.maxPx, d);
        this.stats.hist[d < 2 ? 0 : d < 5 ? 1 : d < 15 ? 2 : 3]++;
      }
      if (d > 48) { this.visOff = { x: 0, y: 0 }; this.stats.snaps++; this.stats.hist[4]++; }
      else { this.visOff.x += dx; this.visOff.y += dy; } // small: smooth visually, collision state already exact
    }
  }

  /** One fixed 60 Hz client step: predict, queue, maybe send. */
  step(cmd: Omit<InputCmd, 'seq'>) {
    if (!this.connected || !this.latest) return;
    const c: InputCmd = { ...cmd, seq: ++this.seq };
    const me = this.me();
    this.prevFoot = { ...this.foot }; if (this.veh) this.prevVeh = { ...this.veh };
    if (me && !me.d && !(me.fx & 2)) {
      if (this.veh && me.v === this.vehId && me.s === 0) {
        const sv = this.myVehicle()!;
        const dead = !!(sv.f & 32);
        stepVehicle(this.map, this.veh, dead ? 0 : -c.my, dead ? 0 : c.mx, VEHICLES[sv.t], this.driveMods(sv.t, me.fx), this.barriers, !dead && !!c.hb);
        this.foot = { x: this.veh.x, y: this.veh.y };
      } else if (!me.v) stepFoot(this.map, this.foot, c.mx, c.my, me.spd, PHYS_DT, footBlockers(this.foot, footCarsOf(this.latest)));
    }
    this.pending.push(c); if (this.pending.length > 240) this.pending.shift();
    this.sendBuf.push(c);
    if (this.sendBuf.length >= 3) { this.sentAt.set(c.seq, performance.now()); this.emitIn(this.sendBuf); this.sendBuf = []; }
    const k = Math.exp(-PHYS_DT * 12); this.visOff.x *= k; this.visOff.y *= k;
  }

  /** Interpolated view of the world at renderTime for remote entities. */
  interp() {
    const rt = this.serverNow() - INTERP_DELAY;
    let a: Snapshot | null = null, b: Snapshot | null = null;
    for (let i = this.snaps.length - 1; i >= 0; i--) { if (this.snaps[i].t <= rt) { a = this.snaps[i]; b = this.snaps[i + 1] ?? null; break; } }
    if (!a) { a = this.snaps[0] ?? null; b = null; }
    const t = a && b ? Math.min(1, Math.max(0, (rt - a.t) / Math.max(1, b.t - a.t))) : 0;
    return { a, b, t };
  }
}

const byId = new WeakMap<object, Map<number, unknown>>();
export function lerpEnt<T extends { id: number; x: number; y: number; a: number }>(listA: T[], listB: T[] | undefined, t: number): T[] {
  if (!listB) return listA;
  let mapA = byId.get(listA) as Map<number, T> | undefined; // the same snapshot list is used for many frames: index it once
  if (!mapA) { mapA = new Map(listA.map(e => [e.id, e])); byId.set(listA, mapA); }
  return listB.map(eb => { const ea = mapA.get(eb.id); if (!ea) return eb; return { ...eb, x: lerp(ea.x, eb.x, t), y: lerp(ea.y, eb.y, t), a: lerpAngle(ea.a, eb.a, t) }; });
}
